/* eslint-disable no-param-reassign */
import Vue from 'vue';
import Vuex from 'vuex';
import createPersistedState from 'vuex-persistedstate';
import SecureLS from 'secure-ls';
import Axios from '@/helpers/axios';
import Swal from 'sweetalert2';
import router from '@/router';

import client from '@/store/client';
import i18n from '../i18n';

const ls = new SecureLS({ isCompression: false });
const VUE_APP_VERSION = require('../../package.json').version;

Vue.use(Vuex);

/* eslint no-shadow: ["error", { "allow": ["state"] }] */
/* eslint no-param-reassign: ["error", { "props": true, "ignorePropertyModificationsFor": ["state"] }] */

const state = {
  VUE_APP_VERSION: process.env.NODE_ENV === 'production' ? `v${VUE_APP_VERSION}` : `v${VUE_APP_VERSION}-dev`,
  user: {
    isVIP: false,
    logged_in: false,
    login_enable: false,
    name: '',
    cell: '',
    avatar: '',
    savedAddress: [],
    mobileBarcodeCarrier: null,
    einvoiceEnable: false,
  },
  vip: {
    coupons: [],
    enable: false,
    points: [],
    vipName: '',
    card: '',
  },

  cart: {
  },
  currentOrder: {
    brand: null,
    store: null,
    id: null,
    detail: null,
    delivery: {
      provider: null,
      fee: 0,
      distance: 0,
      freeDelivery: 0,
      discount: 0,
      waitTime: 0,
    },
  },
  savedOrders: {},

  currentGroup: {
    brand: null,
    store: null,
    id: null,
    host: null,
    owner: false,
  },

  store: {
    name: '',
    branch: '',
    logo: '',
    banner: '',
    order_setting: {
      announce: '',
    },
    payment: [],
    waitTime: 0,
  },
  products: [
    {
      name: '',
      id: '',
      items: [],
    },
  ],
  readOnlyFor: null,
  promos: [],
};

const mutations = {
  SET_USER(state, payload) {
    state.user.logged_in = payload.logged_in;
    state.user.login_enable = payload.login_enable;
    state.user.savedAddress = payload.saved_address;
    state.user.crmEnable = payload.crm_enable;

    if (state.user.logged_in) {
      state.user.name = payload.user.name;
      state.user.cell = payload.user.cell;
      state.user.avatar = payload.user.head_image;
      state.user.mobileBarcodeCarrier = payload.user.mobileBarcodeCarrier;
      state.user.einvoiceEnable = payload.user.einvoice_enable;
    }

    if (payload.vip.is_vip) {
      state.user.isVIP = true;
      state.vip.vipName = payload.vip.vip_name;
      state.vip.card = payload.vip.image_card;
      state.vip.coupons = payload.vip.coupon;
      state.vip.points = payload.vip.points;
    }

    state.user.actions = payload.actions;
  },
  SET_STORE(state, payload) {
    const orderTimeList = [
      {
        label: i18n.t('brand.nowOrder'),
        value: 'ASAP',
      },
      {
        label: i18n.t('brand.scheduleOrder'),
        value: 'SCHEDULED',
      },
    ];

    if (payload.order_setting.DELIVERY) {
      payload.order_setting.DELIVERY.orderTimeList = orderTimeList.filter((element) => payload.order_setting.DELIVERY.orderTime.includes(element.value));

      if (payload.order_setting.DELIVERY.date) {
        const dateArray = payload.order_setting.DELIVERY.date;
        const dateList = [];
        dateArray.forEach((element) => {
          dateList.push({
            label: element,
            value: element,
          });
        });

        payload.order_setting.DELIVERY.date = dateList;
      }
    }

    if (payload.order_setting.PICKUP) {
      payload.order_setting.PICKUP.orderTimeList = orderTimeList.filter((element) => payload.order_setting.PICKUP.orderTime.includes(element.value));

      if (payload.order_setting.PICKUP.date) {
        const dateArray = payload.order_setting.PICKUP.date;
        const dateList = [];
        dateArray.forEach((element) => {
          dateList.push({
            label: element,
            value: element,
          });
        });
        payload.order_setting.PICKUP.date = dateList;
      }
    }

    if (payload.order_setting.INSTORE) {
      payload.order_setting.INSTORE.orderTimeList = orderTimeList.filter((element) => payload.order_setting.INSTORE.orderTime.includes(element.value));

      if (payload.order_setting.INSTORE.date) {
        const dateArray = payload.order_setting.INSTORE.date;
        const dateList = [];
        dateArray.forEach((element) => {
          dateList.push({
            label: element,
            value: element,
          });
        });
        payload.order_setting.INSTORE.date = dateList;
      }
    }

    if (!('groupOrder' in payload.order_setting)) {
      payload.order_setting.groupOrder = true;
    }

    state.store = payload;
  },
  SET_STORE_TIME(state, payload) {
    state.store.order_setting[payload.orderType].time = payload.time;
  },
  SET_STORE_PAYMENT(state, payload) {
    state.store.payment = payload;
  },
  SET_PRODUCTS(state, payload) {
    const oldProducts = JSON.stringify(state.products);
    const newProducts = JSON.stringify(payload);
    if (oldProducts !== newProducts) {
      state.products = payload;
    }
  },
  RESET_PRODUCTS(state) {
    state.products = [];
  },
  SET_READONLY_FOR(state, payload) {
    state.readOnlyFor = payload;
  },
  SET_PROMOS(state, payload) {
    state.promos = payload;
  },
  SET_CURRENT_ORDER(state, payload) {
    state.currentOrder.brand = payload.brand;
    state.currentOrder.store = payload.store;
    state.currentOrder.id = payload.id;
    state.currentOrder.property = payload.property || {};

    if (state.savedOrders[payload.brand]) {
      state.savedOrders[payload.brand][payload.store] = payload.id;
    } else {
      state.savedOrders[payload.brand] = {};
      state.savedOrders[payload.brand] = {
        [payload.store]: payload.id,
      };
    }
  },
  SET_CURRENT_ORDER_DELIVERY(state, payload) {
    state.currentOrder.delivery.provider = payload.provider;
    state.currentOrder.delivery.fee = payload.fee;
    state.currentOrder.delivery.distance = payload.distance;
    state.currentOrder.delivery.discount = payload.discount;
    state.currentOrder.delivery.waitTime = payload.waitTime;

    if (payload.freeDelivery) {
      state.currentOrder.delivery.freeDelivery = payload.freeDelivery;
    }

    if (payload.minimum) {
      state.currentOrder.delivery.minimum = payload.minimum;
    } else {
      state.currentOrder.delivery.minimum = 0;
    }
  },
  RESET_CURRENT_ORDER_DELIVERY(state) {
    state.currentOrder.delivery.provider = null;
    state.currentOrder.delivery.fee = 0;
    state.currentOrder.delivery.distance = 0;
    state.currentOrder.delivery.discount = 0;
    state.currentOrder.delivery.waitTime = 0;

    if (state.currentOrder.delivery.freeDelivery) {
      delete state.currentOrder.delivery.freeDelivery;
    }

    if (state.currentOrder.delivery.minimum) {
      delete state.currentOrder.delivery.minimum;
    }
  },
  RESET_CURRENT_ORDER(state) {
    state.currentOrder = {
      brand: null,
      store: null,
      id: null,
      detail: null,
      delivery: {
        provider: null,
        fee: 0,
        distance: 0,
        freeDelivery: 0,
        waitTime: 0,
      },
    };
  },
  REMOVE_FROM_SAVED_ORDERS(state, payload) {
    if (state.savedOrders[payload.brand]) {
      if (state.savedOrders[payload.brand][payload.store]) {
        delete state.savedOrders[payload.brand][payload.store];
      }
    }
  },
  SET_CURRENT_GROUP(state, payload) {
    state.currentGroup.brand = payload.brand;
    state.currentGroup.store = payload.store;
    state.currentGroup.id = payload.id;
    state.currentGroup.host = payload.host;
    state.currentGroup.owner = payload.owner;
  },
  RESET_CURRENT_GROUP(state) {
    state.currentGroup = {
      brand: null,
      store: null,
      id: null,
      host: null,
      owner: false,
    };
  },
  SET_CART(state, payload) {
    state.currentOrder.detail = payload;
  },
  RESET_CART(state) {
    state.currentOrder = {
      brand: null,
      store: null,
      id: null,
      detail: null,
      delivery: {
        provider: null,
        fee: 0,
        distance: 0,
        freeDelivery: 0,
        discount: 0,
        waitTime: 0,
      },
    };

    state.currentGroup = {
      id: null,
      host: null,
      owner: false,
    };
  },
};

const actions = {
  GET_STORE({ commit }, payload) {
    const data = {
      ...router.query,
      store_id: payload.storeId,
    };

    if (payload.orderId) {
      data.ordre_id = payload.orderId;
    }

    Axios({
      method: 'post',
      url: '/store/get',
      data,
    })
      .then((response) => {
        if (response.data.code !== 500) {
          Swal.fire({
            text: response.data.msg,
          });
          return;
        }
        commit('SET_STORE', response.data.data);
      });
  },
  GET_STORE_HOURS({ commit }, payload) {
    const data = {
      ...router.query,
      store_id: payload.storeId,
      orderType: payload.orderType,
      orderDate: payload.date,
    };

    Axios({
      method: 'post',
      url: '/store/orderHours',
      data,
    })
      .then((response) => {
        if (response.data.code !== 500) {
          Swal.fire({
            text: response.data.msg,
          });
          return;
        }
        commit('SET_STORE_TIME', {
          orderType: payload.orderType,
          time: response.data.data,
        });
      });
  },
  GET_PRODUCTS({ commit }, payload) {
    const data = {
      ...payload.query,
      store_id: payload.storeId,
      orderType: payload.orderType,
    };

    if (payload.orderId) {
      data.order_id = payload.orderId;
    }

    if (payload.time) {
      data.orderDatetime = payload.time;
    }

    Axios({
      method: 'post',
      url: '/menu/get',
      data,
    })
      .then((response) => {
        if (response.data.code !== 500 && response.data.code !== 498) {
          return;
        }

        // 唯讀模式
        if (response.data.code === 498) {
          commit('SET_READONLY_FOR', response.data.msg);
        }

        commit('SET_PRODUCTS', response.data.data.products);

        if (response.data.data.promos) {
          commit('SET_PROMOS', response.data.data.promos);
        }

        if (response.data.data.order) {
          commit('SET_CURRENT_ORDER', {
            brand: router.currentRoute.params.brandId,
            store: router.currentRoute.params.storeId,
            id: response.data.data.order.id,
            property: response.data.data.order.property || null,
          });

          if (response.data.data.order.delivery_provider) {
            const deliveryFeeData = {
              provider: response.data.data.order.delivery_provider,
              fee: response.data.data.order.delivery_fee,
              distance: response.data.data.order.distance,
              discount: response.data.data.order.delivery_fee_discount,
              waitTime: response.data.data.order.waitTime,
            };

            if (response.data.data.order.delivery_minimum) {
              deliveryFeeData.minimum = response.data.data.order.delivery_minimum;
            }

            if (response.data.data.order.free_delivery) {
              deliveryFeeData.freeDelivery = response.data.data.order.free_delivery;
            }

            commit('SET_CURRENT_ORDER_DELIVERY', deliveryFeeData);
          }
        }
      });
  },
  ADD_TO_CART({ commit }, payload) {
    const data = {
      ...router.query,
      store_id: payload.storeId,
      orderType: payload.orderType,
      item_id: payload.itemId,
      count: payload.amount,
      adds: payload.customizeOptions,
    };

    if (payload.cartId) {
      data.order_id = payload.cartId;
    }

    if (payload.time) {
      data.orderDatetime = payload.time;
    }

    if (payload.comment) {
      data.comment = payload.comment;
    }

    return new Promise((resolve, reject) => {
      Axios({
        method: 'post',
        url: '/cart/addItem',
        data,
      })
        .then((response) => {
          if (response.data.code !== 500) {
            Swal.fire({
              text: response.data.msg,
            });
            reject();
            return;
          }
          commit('SET_CART', response.data.data.order);
          resolve();
        });
    });
  },
};

export default new Vuex.Store({
  state,
  mutations,
  actions,
  modules: {
    client,
  },
  plugins: [
    createPersistedState({
      paths: ['currentOrder', 'savedOrders', 'currentGroup'],
      storage: {
        getItem: (key) => ls.get(key),
        setItem: (key, value) => ls.set(key, value),
        removeItem: (key) => ls.remove(key),
      },
    }),
  ],
});
